import { useState } from "react";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import { openInSubBuilder } from "../submittals/navigation";

/** PRC-REV — the results panel. Findings with one-click jumps to the evidence and an
 *  Acknowledge on every high. The panel FLAGS; it never releases or holds the PO. */
export const SEVERITY = {
  high: "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100",
  medium: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100",
  low: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200",
};
export const VERDICT = {
  clear: ["Clear", "bg-emerald-100 text-emerald-800"],
  review: ["Review", "bg-amber-100 text-amber-800"],
  "block-recommended": ["Block recommended", "bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-100"],
};
export const STATE_CHIP = {
  not_run: ["review not run", "bg-slate-100 text-slate-600"],
  skipped: ["review skipped", "bg-slate-100 text-slate-600"],
  clear: ["review clear", "bg-emerald-100 text-emerald-700"],
  findings: ["review findings", "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-100"],
  acknowledged: ["findings acknowledged", "bg-amber-100 text-amber-800"],
  unavailable: ["review unavailable", "bg-slate-200 text-slate-700"],
  stale: ["review out of date", "bg-amber-100 text-amber-800"],
};

/** Jump to the evidence: FRS row, submittal page, quote line, spec section, CO, other PO. */
export const jumpTo = (e, project) => {
  const go = (detail) => window.dispatchEvent(new CustomEvent("abs:ops-jump", { detail }));
  if (e.kind === "frs_row") return go({ tab: "frs", room: e.room, product: e.product });
  if (e.kind === "submittal") return openInSubBuilder({ ops_submittal_number: e.number || e.ref, projectId: project.id });
  if (e.kind === "quote" && e.quote_file_id) return opsApi.quoteFileOpen(e.quote_file_id)
    .catch(() => toast.error("That quote file could not be opened"));
  if (e.kind === "quote") return go({ tab: "estimate" });
  if (e.kind === "spec" && e.document_id) return window.open(opsApi.documentUrl(e.document_id), "_blank", "noopener");
  if (e.kind === "change_order") return go({ tab: "change_orders", co_number: e.co_number });
  if (e.kind === "other_po") return go({ tab: "purchasing", po_number: e.po_number });
  if (e.kind === "po_line") {
    // the model cites the PO line for FRS quantity evidence too — that jump belongs on the FRS grid
    if (/\bfrs\b|room/i.test(e.label || "") && e.frs_rows) return go({ tab: "frs", room: "", product: e.product || "" });
    const el = document.querySelector(`[data-testid="ops-po-line-${e.ref}"]`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("bg-sky-50"); return null; }
  }
  toast.message("No page to jump to for that evidence — it is quoted in the finding above");
  return null;
};

function Finding({ f, project, poId, canAck, onChanged }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ack = async () => {
    setBusy(true);
    try { onChanged(await opsApi.poReviewAck(project.id, poId, { finding_ids: [f.id], reason })); }
    catch (e) { toast.error(e?.response?.data?.detail || "That could not be acknowledged"); }
    finally { setBusy(false); }
  };
  return (
    <li className={`rounded-lg border p-3 ${SEVERITY[f.severity] || SEVERITY.low}`}
        data-testid={`prc-finding-${f.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
        <span data-testid={`prc-finding-severity-${f.id}`}>{f.severity}</span>
        <span className="opacity-70">· {f.category.replace("_", " ")}</span>
        {f.low_confidence && (
          <span className="rounded-full bg-white/70 px-2 py-0.5 normal-case tracking-normal"
                data-testid={`prc-finding-lowconf-${f.id}`}>low confidence · {Math.round(f.confidence * 100)}%</span>
        )}
        {f.source === "rule" && <span className="rounded-full bg-white/70 px-2 py-0.5 normal-case tracking-normal">system check</span>}
        {f.acknowledged && (
          <span className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal"
                data-testid={`prc-finding-acked-${f.id}`}>
            <Check className="h-3 w-3" /> acknowledged by {f.acknowledged.by}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs font-semibold" data-testid={`prc-finding-what-${f.id}`}>{f.what}</p>
      {f.suggested_fix && <p className="mt-0.5 text-xs opacity-80">→ {f.suggested_fix}</p>}
      {f.acknowledged?.reason && <p className="mt-0.5 text-xs italic opacity-80">“{f.acknowledged.reason}”</p>}
      {f.evidence?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {f.evidence.map((e, i) => (
            <button key={i} onClick={() => jumpTo(e, project)} disabled={!e.resolved && e.kind !== "po_line"}
                    data-testid={`prc-evidence-${f.id}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border border-current/20 bg-white/70 px-2 py-0.5 text-[11px] hover:bg-white disabled:opacity-50">
              {e.label || e.ref} <ArrowUpRight className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
      {f.severity === "high" && !f.acknowledged && canAck && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
                 className="h-7 max-w-xs bg-white text-xs" data-testid={`prc-ack-reason-${f.id}`} />
          <Button size="sm" variant="outline" onClick={ack} disabled={busy} className="h-7 bg-white text-xs"
                  data-testid={`prc-ack-${f.id}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Acknowledge"}
          </Button>
        </div>
      )}
    </li>
  );
}

export default function ReviewFindings({ gate, project, poId, canAck, onChanged }) {
  const r = gate?.review;
  if (!r) return null;
  const [vLabel, vTone] = VERDICT[r.verdict] || ["", "bg-slate-100 text-slate-700"];
  return (
    <div className="space-y-2" data-testid="prc-results">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {r.state === "unavailable" ? (
          <span className="rounded-full bg-slate-200 px-2 py-0.5 font-medium text-slate-800"
                data-testid="prc-unavailable">Review unavailable</span>
        ) : (
          <span className={`rounded-full px-2 py-0.5 font-medium ${vTone}`} data-testid="prc-verdict">{vLabel}</span>
        )}
        <span className="text-slate-500">
          {r.auto ? "ran automatically" : r.mode === "prompt" ? "ran on request" : "ran manually"} ·{" "}
          {(r.ran_at || "").slice(0, 16).replace("T", " ")} · {r.ran_by}
          {r.model && r.state !== "unavailable" && ` · ${r.model}`}
          {r.tokens?.prompt_tokens ? ` · ${r.tokens.prompt_tokens + (r.tokens.completion_tokens || 0)} tokens` : ""}
        </span>
        {!gate.review_current && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800" data-testid="prc-stale">
            the PO, quote, submittals or FRS changed since — re-run before sending
          </span>
        )}
      </div>
      {r.state === "unavailable" && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700"
             data-testid="prc-unavailable-panel">
          <p>{r.reason}</p>
          {r.acknowledged ? (
            <p className="mt-1 inline-flex items-center gap-1 text-slate-600" data-testid="prc-unavailable-acked">
              <Check className="h-3 w-3" /> acknowledged by {r.acknowledged.by}
              {r.acknowledged.reason && ` — “${r.acknowledged.reason}”`}
            </p>
          ) : canAck && gate.mode === "auto" && <UnavailableAck project={project} poId={poId} onChanged={onChanged} />}
        </div>
      )}
      {r.findings?.length > 0 && (
        <ul className="space-y-2" data-testid="prc-findings">
          {r.findings.map((f) => <Finding key={f.id} f={f} project={project} poId={poId} canAck={canAck} onChanged={onChanged} />)}
        </ul>
      )}
      {r.state !== "unavailable" && !r.findings?.length && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
           data-testid="prc-clear">No findings. The PO agrees with the submittal, the FRS, the quote and the change orders it was checked against.</p>
      )}
    </div>
  );
}

function UnavailableAck({ project, poId, onChanged }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ack = async () => {
    setBusy(true);
    try { onChanged(await opsApi.poReviewAck(project.id, poId, { reason })); }
    catch (e) { toast.error(e?.response?.data?.detail || "That could not be acknowledged"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
             className="h-7 max-w-xs bg-white text-xs" data-testid="prc-unavailable-reason" />
      <Button size="sm" variant="outline" onClick={ack} disabled={busy} className="h-7 bg-white text-xs"
              data-testid="prc-unavailable-ack">Acknowledge and proceed without the review</Button>
    </div>
  );
}
